import { logTransition } from "./transition_debug"

let sharedContext = null
let mediaUnlocked = false
let beepUrl = null
let chimeNoteUrls = null

const SAMPLE_RATE = 44_100

function audioContextCtor() {
  return window.AudioContext || window.webkitAudioContext || null
}

function existingContext() {
  return sharedContext && sharedContext.state !== "closed" ? sharedContext : null
}

export function isAudioUnlocked() {
  return mediaUnlocked || existingContext()?.state === "running"
}

// Only call from a user-gesture handler (Play, Resume, Enable notifications).
export async function resumeAudioContext({ allowCreate = true } = {}) {
  const Ctor = audioContextCtor()
  if (!Ctor) return null

  if (!existingContext()) {
    if (!allowCreate) return null

    sharedContext = new Ctor()
    logTransition("AudioContext created after user gesture", { state: sharedContext.state })
  }

  const ctx = existingContext()
  if (!ctx) return null

  if (ctx.state === "suspended") {
    try {
      await ctx.resume()
      logTransition("AudioContext resumed", { state: ctx.state })
    } catch (error) {
      logTransition("AudioContext resume failed", { error: String(error), state: ctx.state })
    }
  }

  return ctx
}

function createWavBlob(samples) {
  const numSamples = samples.length
  const buffer = new ArrayBuffer(44 + numSamples * 2)
  const view = new DataView(buffer)

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeString(0, "RIFF")
  view.setUint32(4, 36 + numSamples * 2, true)
  writeString(8, "WAVE")
  writeString(12, "fmt ")
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, SAMPLE_RATE * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeString(36, "data")
  view.setUint32(40, numSamples * 2, true)

  let offset = 44
  for (let i = 0; i < numSamples; i++) {
    view.setInt16(offset, samples[i], true)
    offset += 2
  }

  return new Blob([buffer], { type: "audio/wav" })
}

function sineWave(frequency, durationMs, peak = 0.25) {
  const n = Math.ceil((SAMPLE_RATE * durationMs) / 1000)
  const samples = new Int16Array(n)
  const attackSamples = Math.floor(SAMPLE_RATE * 0.012)
  const releaseSamples = Math.floor(SAMPLE_RATE * 0.05)

  for (let i = 0; i < n; i++) {
    const t = i / SAMPLE_RATE
    let envelope = 1

    if (i < attackSamples) {
      envelope = i / attackSamples
    } else if (i > n - releaseSamples) {
      envelope = (n - i) / releaseSamples
    }

    samples[i] = Math.round(Math.sin(2 * Math.PI * frequency * t) * envelope * peak * 32_767)
  }

  return samples
}

function ensureSoundUrls() {
  if (beepUrl) return

  beepUrl = URL.createObjectURL(createWavBlob(sineWave(880, 90, 0.14)))
  chimeNoteUrls = [
    { url: URL.createObjectURL(createWavBlob(sineWave(523.25, 220))), delay: 0 },
    { url: URL.createObjectURL(createWavBlob(sineWave(659.25, 220))), delay: 170 },
    { url: URL.createObjectURL(createWavBlob(sineWave(783.99, 300))), delay: 340 }
  ]
}

async function playMediaUrl(url, volume = 1) {
  const audio = new Audio(url)
  audio.volume = volume

  try {
    await audio.play()
    return true
  } catch (_error) {
    return false
  }
}

// HTMLMediaElement unlock survives background tabs after a user-gesture play().
// Web Audio must be created/resumed inside that same gesture — see Chrome autoplay policy.
export async function warmUpMediaPlayback({ allowCreate = true } = {}) {
  ensureSoundUrls()

  const ctx = await resumeAudioContext({ allowCreate })

  if (!mediaUnlocked) {
    const audio = new Audio(beepUrl)
    audio.volume = 0.001

    try {
      await audio.play()
      audio.pause()
      audio.currentTime = 0
      mediaUnlocked = true
      logTransition("audio unlocked via media warmup", {
        audioContextState: ctx?.state ?? "none",
        allowCreate
      })
    } catch (error) {
      logTransition("media warmup failed", { error: String(error), allowCreate })
    }
  }

  return mediaUnlocked || ctx?.state === "running"
}

export function playCountdownBeep() {
  ensureSoundUrls()

  if (mediaUnlocked) {
    void playMediaUrl(beepUrl)
    return true
  }

  const ctx = existingContext()
  if (!ctx || ctx.state !== "running") return false

  try {
    scheduleBellNote(ctx, 880, ctx.currentTime, 0.09, 0.14)
    return true
  } catch (_error) {
    return false
  }
}

export function playTransitionChime() {
  logTransition("playTransitionChime called", {
    mediaUnlocked,
    audioContextState: existingContext()?.state ?? "none"
  })
  void playTransitionChimeAsync()
}

async function playTransitionChimeAsync() {
  ensureSoundUrls()

  if (mediaUnlocked) {
    const results = await Promise.all(
      chimeNoteUrls.map(
        ({ url, delay }) =>
          new Promise((resolve) => {
            setTimeout(() => {
              resolve(playMediaUrl(url))
            }, delay)
          })
      )
    )

    if (results.some(Boolean)) {
      logTransition("transition chime played via media", { notes: results.filter(Boolean).length })
      return true
    }

    logTransition("transition chime media playback failed", { results })
  }

  if (document.hidden) {
    logTransition("transition chime skipped in background", {
      reason: "Web Audio suspended and media playback failed — press Play while on this tab first"
    })
    return false
  }

  const ctx = existingContext()
  if (!ctx || ctx.state !== "running") {
    logTransition("transition chime skipped", {
      reason: ctx ? `audio context ${ctx.state}` : "no audio context — press Play first"
    })
    return false
  }

  try {
    const now = ctx.currentTime
    const notes = [
      { frequency: 523.25, at: 0, duration: 0.22 },
      { frequency: 659.25, at: 0.17, duration: 0.22 },
      { frequency: 783.99, at: 0.34, duration: 0.3 }
    ]

    notes.forEach(({ frequency, at, duration }) => {
      scheduleBellNote(ctx, frequency, now + at, duration)
    })

    logTransition("transition chime played via Web Audio", { notes: notes.length })
    return true
  } catch (error) {
    logTransition("transition chime failed", { error: String(error) })
    return false
  }
}

function scheduleBellNote(ctx, frequency, startTime, duration, peakGain = 0.11) {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()

  oscillator.type = "sine"
  oscillator.frequency.value = frequency
  oscillator.connect(gain)
  gain.connect(ctx.destination)

  const attack = 0.012
  const endTime = startTime + duration

  gain.gain.setValueAtTime(0.0001, startTime)
  gain.gain.linearRampToValueAtTime(peakGain, startTime + attack)
  gain.gain.exponentialRampToValueAtTime(0.0001, endTime)

  oscillator.start(startTime)
  oscillator.stop(endTime + 0.04)
}
